type PuppyKernel = ReturnType<typeof import('../../engine/loader').getPuppyKernel>;

type SameDomainUnifier = {
  AllowInternalEdges?: (allow: boolean) => void;
  Build?: () => void;
  Shape?: () => unknown;
  History?: () => Record<string, unknown> | null;
  delete?: () => void;
};

type SameDomainUnifierConstructor = new (
  shape: unknown,
  unifyEdges?: boolean,
  unifyFaces?: boolean,
  concatBSplines?: boolean,
) => SameDomainUnifier;

/**
 * SameDomain 同域归并输入。
 */
export interface SameDomainUnificationInput {
  /** Puppy CAD 内核 实例。 */
  puppy: PuppyKernel;
  /** 需要归并同域面/边的 Shape。 */
  shape: unknown;
  /** 错误消息中的操作名称。 */
  operationLabel: string;
  /** 是否允许拼接相邻 BSpline 面。 */
  concatBSplines?: boolean;
  /** 是否保留内部边；SolidWorks 风格实体合并默认移除内部边。 */
  allowInternalEdges?: boolean;
}

/**
 * SameDomain 同域归并结果。
 */
export interface SameDomainUnificationResult {
  /** 归并后的 Shape；缺少绑定能力时为输入 Shape。 */
  shape: unknown;
  /** Puppy CAD 内核 同域归并 history；缺少绑定能力时为 null。 */
  history: Record<string, unknown> | null;
}

/**
 * 安全释放 Puppy CAD 内核 包装对象。
 *
 * @param value 待释放对象。
 */
function safeDelete(value: unknown): void {
  const deleteMethod = (value as { delete?: () => void } | null)?.delete;
  if (typeof deleteMethod !== 'function') {
    return;
  }

  try {
    deleteMethod.call(value);
  } catch {
    /*
     * Puppy CAD 内核 包装对象释放失败不能覆盖几何构建的主错误。
     * 调用方只依赖最终 shape 和 history，释放异常在这里明确兜底。
     */
  }
}

/**
 * 解析 Puppy CAD 内核 同域归并构造器。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @returns 同域归并构造器；缺少绑定时返回 null。
 */
function resolveSameDomainUnifierConstructor(
  puppy: PuppyKernel,
): SameDomainUnifierConstructor | null {
  const kernelRecord = puppy as Record<string, unknown>;
  const ctor = kernelRecord.ShapeUpgrade_UnifySameDomain_2
    ?? kernelRecord.ShapeUpgrade_UnifySameDomain_1
    ?? kernelRecord.ShapeUpgrade_UnifySameDomain;
  return typeof ctor === 'function' ? ctor as SameDomainUnifierConstructor : null;
}

/**
 * 在 SameDomain unifier 存活期间消费归并结果。
 *
 * 设计说明：
 * 1. Merge Result 需要同时读取归并后的 shape 和 unifier history；
 * 2. history 可能依赖 unifier 生命周期，因此通过回调限定消费窗口；
 * 3. 普通 blend 链路只需要 shape，可通过 `unifySameDomainShape` 复用该入口。
 *
 * @param input 同域归并输入。
 * @param consumeResult 结果消费回调。
 * @returns 回调返回值。
 */
export function withSameDomainUnification<T>(
  input: SameDomainUnificationInput,
  consumeResult: (result: SameDomainUnificationResult) => T,
): T {
  const unifierCtor = resolveSameDomainUnifierConstructor(input.puppy);
  if (!unifierCtor) {
    return consumeResult({
      shape: input.shape,
      history: null,
    });
  }

  const constructorArguments: Array<[unknown, boolean?, boolean?, boolean?]> = [
    [input.shape, true, true, input.concatBSplines === true],
    [input.shape],
  ];
  let constructorError: Error | null = null;

  for (const args of constructorArguments) {
    let unifier: SameDomainUnifier | null = null;
    let history: Record<string, unknown> | null = null;
    try {
      unifier = new unifierCtor(...args);
    } catch (error) {
      constructorError = error instanceof Error ? error : new Error(String(error));
      continue;
    }

    try {
      unifier.AllowInternalEdges?.(input.allowInternalEdges === true);
      unifier.Build?.();
      const unifiedShape = unifier.Shape?.() ?? null;
      if (!unifiedShape) {
        throw new Error(`${input.operationLabel} 同域归并失败，未返回有效 Shape`);
      }
      history = unifier.History?.() ?? null;
      return consumeResult({
        shape: unifiedShape,
        history,
      });
    } finally {
      safeDelete(history);
      safeDelete(unifier);
    }
  }

  throw new Error(`${input.operationLabel} 同域归并构造失败：${constructorError?.message ?? '未知错误'}`);
}

/**
 * 对 Shape 执行 SameDomain 同域归并。
 *
 * @param input 同域归并输入。
 * @returns 归并后的 Shape；缺少绑定能力时返回原 Shape。
 */
export function unifySameDomainShape(input: SameDomainUnificationInput): unknown {
  return withSameDomainUnification(input, (result) => result.shape);
}
